import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { db as supabaseUntyped } from "@/lib/supabaseUntyped";
import { getShareUrl } from "@/lib/shareUrl";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { 
  ArrowLeft, 
  Link2, 
  MoreVertical, 
  Trash2, 
  Copy, 
  ExternalLink,
  Eye,
  Calendar,
  Shield,
  FileText,
  Loader2,
  MessageCircle,
  Send,
  Image as ImageIcon,
  BarChart3,
  FolderOpen,
  Files,
  CheckSquare,
  Square,
  X,
  Clock,
  CalendarClock,
  Infinity as InfinityIcon
} from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { format, formatDistanceToNow, isPast, addDays, addMonths } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "@/lib/i18n/useTranslation";
import { ShareTrackingStats } from "@/components/demarches/ShareTrackingStats";
import type { Tables } from "@/integrations/supabase/types";

type ShareLink = Tables<"share_links"> & {
  documents: { title: string; file_name: string } | null;
  comment_count: number;
  unread_count: number;
  last_comment_at: string | null;
  type: "document";
};

type DocumentCollection = {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  token: string;
  permission: string;
  expires_at: string | null;
  view_count: number;
  created_at: string;
  updated_at: string;
  document_count: number;
  type: "collection";
};

type CombinedLink = ShareLink | DocumentCollection;

type Comment = {
  id: string;
  body: string;
  created_at: string;
  author_name: string | null;
  author_email: string | null;
  user_id: string | null;
  image_url: string | null;
  is_external: boolean;
};

// Helper to get last read timestamp from localStorage
const getLastReadTimestamp = (linkId: string): string | null => {
  try {
    const data = localStorage.getItem('share_links_read');
    if (data) {
      const parsed = JSON.parse(data);
      return parsed[linkId] || null;
    }
  } catch (e) {
    console.error('Error reading localStorage:', e);
  }
  return null;
};

// Helper to set last read timestamp in localStorage
const setLastReadTimestamp = (linkId: string, timestamp: string) => {
  try {
    const data = localStorage.getItem('share_links_read');
    const parsed = data ? JSON.parse(data) : {};
    parsed[linkId] = timestamp;
    localStorage.setItem('share_links_read', JSON.stringify(parsed));
  } catch (e) {
    console.error('Error writing localStorage:', e);
  }
};

const ShareLinks = () => {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [collections, setCollections] = useState<DocumentCollection[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteType, setDeleteType] = useState<"document" | "collection">("document");
  const [deleting, setDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState<"all" | "documents" | "collections">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "expired">("all");
  
  // Multi-selection state
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  
  // Expiration edit state
  const [editingItem, setEditingItem] = useState<CombinedLink | null>(null);
  const [expirationOption, setExpirationOption] = useState<string>("7days");
  const [customDate, setCustomDate] = useState<string>("");
  const [savingExpiration, setSavingExpiration] = useState(false);
  
  // Discussion panel state
  const [selectedLink, setSelectedLink] = useState<ShareLink | null>(null);
  const [selectedCollection, setSelectedCollection] = useState<DocumentCollection | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [documentIds, setDocumentIds] = useState<string[]>([]);

  useEffect(() => {
    fetchLinks();
    fetchCollections();
    getCurrentUser();
  }, []);

  // Real-time subscription for new comments
  useEffect(() => {
    if (documentIds.length === 0) return;

    const channel = supabase
      .channel('share-links-comments')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'comments',
        },
        (payload) => {
          const newComment = payload.new as Comment & { document_id: string; share_link_id: string };
          
          // Check if this comment is for one of our shared documents
          if (documentIds.includes(newComment.document_id)) {
            // Show toast notification for external comments
            if (newComment.is_external) {
              const link = links.find(l => l.document_id === newComment.document_id);
              toast.info(
                `💬 Nouveau commentaire sur "${link?.documents?.title || 'un document'}"`,
                {
                  description: newComment.body.substring(0, 50) + (newComment.body.length > 50 ? '...' : ''),
                  action: {
                    label: "Voir",
                    onClick: () => {
                      if (link) fetchComments(link);
                    },
                  },
                }
              );
            }
            
            // Update comment count and unread count in links list
            setLinks(prevLinks => 
              prevLinks.map(l => {
                if (l.document_id === newComment.document_id) {
                  // If panel is not open for this link, increment unread
                  const isOpen = selectedLink?.id === l.id;
                  return { 
                    ...l, 
                    comment_count: l.comment_count + 1,
                    unread_count: newComment.is_external && !isOpen ? l.unread_count + 1 : l.unread_count,
                    last_comment_at: newComment.created_at,
                  };
                }
                return l;
              })
            );
            
            // If the discussion panel is open for this document, add the comment
            if (selectedLink?.document_id === newComment.document_id) {
              setComments(prev => [...prev, {
                id: newComment.id,
                body: newComment.body,
                created_at: newComment.created_at,
                author_name: newComment.author_name,
                author_email: newComment.author_email,
                user_id: newComment.user_id,
                image_url: newComment.image_url,
                is_external: newComment.is_external,
              }]);
            }
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [documentIds, links, selectedLink]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchLinks = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    // Fetch share links with documents
    const { data: linksData, error: linksError } = await supabase
      .from("share_links")
      .select("*, documents(title, file_name)")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (linksError) {
      console.error("Error fetching share links:", linksError);
      toast.error(t.shareLinks.toasts.loadError);
      return;
    }

    // Fetch comment counts and last comment time for each link
    const linksWithCounts = await Promise.all(
      (linksData || []).map(async (link) => {
        // Get comment count
        const { count } = await supabase
          .from("comments")
          .select("*", { count: "exact", head: true })
          .eq("share_link_id", link.id);
        
        // Get last external comment timestamp
        const { data: lastComment } = await supabase
          .from("comments")
          .select("created_at")
          .eq("share_link_id", link.id)
          .eq("is_external", true)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        
        const lastReadTimestamp = getLastReadTimestamp(link.id);
        const lastCommentAt = lastComment?.created_at || null;
        
        // Calculate unread count (external comments after last read)
        let unreadCount = 0;
        if (lastCommentAt && (!lastReadTimestamp || new Date(lastCommentAt) > new Date(lastReadTimestamp))) {
          const { count: unread } = await supabase
            .from("comments")
            .select("*", { count: "exact", head: true })
            .eq("share_link_id", link.id)
            .eq("is_external", true)
            .gt("created_at", lastReadTimestamp || '1970-01-01');
          unreadCount = unread || 0;
        }
        
        return {
          ...link,
          comment_count: count || 0,
          unread_count: unreadCount,
          last_comment_at: lastCommentAt,
          type: "document" as const,
        };
      })
    );

    setLinks(linksWithCounts);
    
    // Store document IDs for realtime subscription
    const docIds = linksWithCounts.map(l => l.document_id).filter(Boolean);
    setDocumentIds(docIds);
  };

  const fetchCollections = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setLoading(false);
      return;
    }

    // Fetch document collections using untyped client
    const { data: collectionsData, error: collectionsError } = await supabaseUntyped
      .from("document_collections")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (collectionsError) {
      console.error("Error fetching collections:", collectionsError);
    } else if (collectionsData) {
      // Fetch document counts for each collection
      const collectionsWithCounts = await Promise.all(
        collectionsData.map(async (collection: any) => {
          const { count } = await supabaseUntyped
            .from("collection_documents")
            .select("*", { count: "exact", head: true })
            .eq("collection_id", collection.id);
          
          return {
            ...collection,
            document_count: count || 0,
            type: "collection" as const,
          };
        })
      );
      setCollections(collectionsWithCounts);
    }

    setLoading(false);
  };

  const fetchComments = async (link: ShareLink) => {
    setLoadingComments(true);
    setSelectedLink(link);
    setSelectedCollection(null);
    
    // Mark as read when opening discussion
    const now = new Date().toISOString();
    setLastReadTimestamp(link.id, now);
    
    // Update unread count in state
    setLinks(prev => prev.map(l => 
      l.id === link.id ? { ...l, unread_count: 0 } : l
    ));

    const { data, error } = await supabase
      .from("comments")
      .select("id, body, created_at, author_name, author_email, user_id, image_url, is_external")
      .eq("document_id", link.document_id)
      .order("created_at", { ascending: true });

    if (error) {
      console.error("Error fetching comments:", error);
      toast.error(t.shareLinks.toasts.commentsLoadError);
    } else {
      setComments(data || []);
    }
    setLoadingComments(false);
  };

  const openCollectionStats = (collection: DocumentCollection) => {
    setSelectedCollection(collection);
    setSelectedLink(null);
    setComments([]);
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedLink || !currentUserId) return;

    setSending(true);
    const { error } = await supabase
      .from("comments")
      .insert({
        document_id: selectedLink.document_id,
        body: replyText.trim(),
        user_id: currentUserId,
        is_external: false,
      });

    if (error) {
      console.error("Error sending reply:", error);
      toast.error(t.shareLinks.toasts.sendError);
    } else {
      toast.success(t.shareLinks.toasts.replySent);
      setReplyText("");
      // Refresh comments
      fetchComments(selectedLink);
      // Update comment count in list
      setLinks(links.map(l => 
        l.id === selectedLink.id 
          ? { ...l, comment_count: l.comment_count + 1 }
          : l
      ));
    }
    setSending(false);
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    setDeleting(true);
    
    if (deleteType === "document") {
      const { error } = await supabase
        .from("share_links")
        .delete()
        .eq("id", deleteId);

      if (error) {
        console.error("Error deleting share link:", error);
        toast.error(t.shareLinks.toasts.deleteError);
      } else {
        toast.success(t.shareLinks.toasts.deleteSuccess);
        setLinks(links.filter(l => l.id !== deleteId));
      }
    } else {
      // Delete collection
      const { error } = await supabaseUntyped
        .from("document_collections")
        .delete()
        .eq("id", deleteId);

      if (error) {
        console.error("Error deleting collection:", error);
        toast.error(t.shareLinks.toasts.deleteError);
      } else {
        toast.success(t.shareLinks.toasts.deleteSuccess);
        setCollections(collections.filter(c => c.id !== deleteId));
      }
    }
    
    setDeleting(false);
    setDeleteId(null);
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    
    setDeleting(true);
    
    const docIdsToDelete = links.filter(l => selectedItems.has(l.id)).map(l => l.id);
    const collectionIdsToDelete = collections.filter(c => selectedItems.has(c.id)).map(c => c.id);
    
    let hasError = false;
    
    // Delete documents
    if (docIdsToDelete.length > 0) {
      const { error } = await supabase
        .from("share_links")
        .delete()
        .in("id", docIdsToDelete);
      
      if (error) {
        console.error("Error bulk deleting share links:", error);
        hasError = true;
      }
    }
    
    // Delete collections
    if (collectionIdsToDelete.length > 0) {
      const { error } = await supabaseUntyped
        .from("document_collections")
        .delete()
        .in("id", collectionIdsToDelete);
      
      if (error) {
        console.error("Error bulk deleting collections:", error);
        hasError = true;
      }
    }
    
    if (hasError) {
      toast.error(t.shareLinks.toasts.deleteError);
    } else {
      toast.success(`${selectedItems.size} liens révoqués`);
      setLinks(links.filter(l => !selectedItems.has(l.id)));
      setCollections(collections.filter(c => !selectedItems.has(c.id)));
    }
    
    setDeleting(false);
    setShowBulkDelete(false);
    setSelectedItems(new Set());
  };

  // Toggle selection for an item
  const toggleSelection = (id: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select/deselect all visible items
  const toggleSelectAll = () => {
    if (selectedItems.size === displayedItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(displayedItems.map(item => item.id)));
    }
  };

  // Open expiration edit dialog
  const openExpirationEdit = (item: CombinedLink) => {
    setEditingItem(item);
    // Set current value
    if (item.expires_at) {
      setExpirationOption("custom");
      setCustomDate(format(new Date(item.expires_at), "yyyy-MM-dd"));
    } else {
      setExpirationOption("never");
      setCustomDate("");
    }
  };

  // Calculate new expiration date based on option
  const getNewExpirationDate = (): string | null => {
    const now = new Date();
    switch (expirationOption) {
      case "7days":
        return addDays(now, 7).toISOString();
      case "30days":
        return addDays(now, 30).toISOString();
      case "3months":
        return addMonths(now, 3).toISOString();
      case "1year":
        return addMonths(now, 12).toISOString();
      case "custom":
        return customDate ? new Date(customDate + "T23:59:59").toISOString() : null;
      case "never":
      default:
        return null;
    }
  };

  // Save expiration date
  const handleSaveExpiration = async () => {
    if (!editingItem) return;
    
    setSavingExpiration(true);
    const newExpiresAt = getNewExpirationDate();
    const isCollection = editingItem.type === "collection";
    
    if (isCollection) {
      const { error } = await supabaseUntyped
        .from("document_collections")
        .update({ expires_at: newExpiresAt })
        .eq("id", editingItem.id);
      
      if (error) {
        console.error("Error updating collection expiration:", error);
        toast.error("Erreur lors de la modification");
      } else {
        toast.success("Date d'expiration modifiée");
        setCollections(collections.map(c => 
          c.id === editingItem.id ? { ...c, expires_at: newExpiresAt } : c
        ));
      }
    } else {
      const { error } = await supabase
        .from("share_links")
        .update({ expires_at: newExpiresAt })
        .eq("id", editingItem.id);
      
      if (error) {
        console.error("Error updating share link expiration:", error);
        toast.error("Erreur lors de la modification");
      } else {
        toast.success("Date d'expiration modifiée");
        setLinks(links.map(l => 
          l.id === editingItem.id ? { ...l, expires_at: newExpiresAt } : l
        ));
      }
    }
    
    setSavingExpiration(false);
    setEditingItem(null);
  };

  const copyLink = (token: string, isCollection: boolean = false) => {
    const url = isCollection 
      ? getShareUrl(token, true) 
      : getShareUrl(token);
    navigator.clipboard.writeText(url);
    toast.success(t.share.linkCopiedClipboard);
  };

  const openLink = (token: string, isCollection: boolean = false) => {
    const path = isCollection ? `/share/collection/${token}` : `/share/${token}`;
    window.open(path, "_blank");
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return isPast(new Date(expiresAt));
  };

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "d MMM yyyy", { locale: lang === 'fr' ? fr : enUS });
  };

  const formatRelative = (dateStr: string) => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: lang === 'fr' ? fr : enUS });
  };

  const getInitials = (name: string | null, email: string | null) => {
    if (name) return name.substring(0, 2).toUpperCase();
    if (email) return email.substring(0, 2).toUpperCase();
    return "?";
  };

  const totalComments = links.reduce((sum, l) => sum + l.comment_count, 0);
  
  // Combined and filtered items
  const allItems: CombinedLink[] = [
    ...links,
    ...collections,
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  
  // Apply type filter
  const typeFilteredItems = activeTab === "all" 
    ? allItems 
    : activeTab === "documents" 
      ? links 
      : collections;
  
  // Apply status filter
  const displayedItems = statusFilter === "all"
    ? typeFilteredItems
    : statusFilter === "active"
      ? typeFilteredItems.filter(item => !isExpired(item.expires_at))
      : typeFilteredItems.filter(item => isExpired(item.expires_at));

  // Count active and expired for filter badges
  const activeCount = typeFilteredItems.filter(item => !isExpired(item.expires_at)).length;
  const expiredCount = typeFilteredItems.filter(item => isExpired(item.expires_at)).length;

  const totalViews = links.reduce((sum, l) => sum + l.view_count, 0) + 
                     collections.reduce((sum, c) => sum + c.view_count, 0);

  return (
    <div className="min-h-screen bg-background md:ml-16">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border safe-area-top">
        <div className="container mx-auto px-4 h-14 md:h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 md:gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="flex-shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <Link2 className="w-4 h-4 text-primary" />
              </div>
              <h1 className="text-lg md:text-xl font-semibold">{t.shareLinks.title}</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-4 md:py-8 pb-24 md:pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (links.length === 0 && collections.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-16 md:py-20 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
              <Link2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-lg md:text-xl font-semibold mb-2">{t.shareLinks.noLinks}</h2>
            <p className="text-sm md:text-base text-muted-foreground mb-6">
              {t.shareLinks.noLinksDesc}
            </p>
            <Link to="/documents">
              <Button>{t.shareLinks.goToDocuments}</Button>
            </Link>
          </div>
        ) : (
          <>
            {/* Filter tabs */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
              <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v as any); setSelectedItems(new Set()); }} className="flex-1">
                <TabsList className="grid w-full grid-cols-3 max-w-md">
                  <TabsTrigger value="all" className="gap-1.5">
                    <Link2 className="w-4 h-4" />
                    Tous ({allItems.length})
                  </TabsTrigger>
                  <TabsTrigger value="documents" className="gap-1.5">
                    <FileText className="w-4 h-4" />
                    Documents ({links.length})
                  </TabsTrigger>
                  <TabsTrigger value="collections" className="gap-1.5">
                    <FolderOpen className="w-4 h-4" />
                    Dossiers ({collections.length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              {/* Status Filter */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground hidden sm:block">Statut:</span>
                <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                  <Button
                    variant={statusFilter === "all" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    onClick={() => { setStatusFilter("all"); setSelectedItems(new Set()); }}
                  >
                    Tous
                  </Button>
                  <Button
                    variant={statusFilter === "active" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 px-2.5 text-xs gap-1"
                    onClick={() => { setStatusFilter("active"); setSelectedItems(new Set()); }}
                  >
                    <span className="w-2 h-2 rounded-full bg-success" />
                    Actifs ({activeCount})
                  </Button>
                  <Button
                    variant={statusFilter === "expired" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 px-2.5 text-xs gap-1"
                    onClick={() => { setStatusFilter("expired"); setSelectedItems(new Set()); }}
                  >
                    <span className="w-2 h-2 rounded-full bg-destructive" />
                    Expirés ({expiredCount})
                  </Button>
                </div>
              </div>
            </div>

            {/* Empty state for filtered results */}
            {displayedItems.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                  {statusFilter === "expired" ? (
                    <Clock className="w-6 h-6 text-muted-foreground" />
                  ) : (
                    <Eye className="w-6 h-6 text-muted-foreground" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">
                  {statusFilter === "expired" 
                    ? "Aucun lien expiré" 
                    : statusFilter === "active"
                      ? "Aucun lien actif"
                      : "Aucun lien trouvé"
                  }
                </p>
                {statusFilter !== "all" && (
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={() => setStatusFilter("all")}
                    className="mt-2"
                  >
                    Voir tous les liens
                  </Button>
                )}
              </div>
            )}

            {/* Bulk Actions Bar */}
            {selectedItems.size > 0 && (
              <div className="mb-4 p-3 bg-primary/10 border border-primary/20 rounded-xl flex items-center justify-between gap-3 animate-in slide-in-from-top-2 duration-200">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedItems(new Set())}
                    className="h-8 px-2"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                  <span className="text-sm font-medium">
                    {selectedItems.size} sélectionné{selectedItems.size > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={toggleSelectAll}
                    className="gap-1.5"
                  >
                    {selectedItems.size === displayedItems.length ? (
                      <>
                        <Square className="w-4 h-4" />
                        Tout désélectionner
                      </>
                    ) : (
                      <>
                        <CheckSquare className="w-4 h-4" />
                        Tout sélectionner
                      </>
                    )}
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowBulkDelete(true)}
                    className="gap-1.5"
                  >
                    <Trash2 className="w-4 h-4" />
                    Révoquer ({selectedItems.size})
                  </Button>
                </div>
              </div>
            )}

            {/* Mobile Card View */}
            {displayedItems.length > 0 && (
            <div className="md:hidden space-y-3">
              {displayedItems.map((item) => {
                const expired = isExpired(item.expires_at);
                const isCollection = item.type === "collection";
                const isSelected = selectedItems.has(item.id);
                
                return (
                  <div 
                    key={item.id} 
                    className={`bg-card border rounded-xl p-4 transition-colors ${
                      isSelected ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(item.id)}
                          className="shrink-0"
                        />
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          isCollection ? "bg-amber-500/10" : "bg-primary/10"
                        }`}>
                          {isCollection ? (
                            <FolderOpen className="w-5 h-5 text-amber-500" />
                          ) : (
                            <FileText className="w-5 h-5 text-primary" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate text-sm">
                            {isCollection 
                              ? (item as DocumentCollection).name
                              : ((item as ShareLink).documents?.title || t.shareLinks.deletedDocument)
                            }
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {isCollection 
                              ? `${(item as DocumentCollection).document_count} documents`
                              : (item as ShareLink).documents?.file_name
                            }
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon-sm">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => copyLink(item.token, isCollection)}>
                            <Copy className="w-4 h-4 mr-2" />
                            {t.shareLinks.copyLink}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openLink(item.token, isCollection)}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            {t.shareLinks.openLink}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openExpirationEdit(item)}>
                            <CalendarClock className="w-4 h-4 mr-2" />
                            Modifier l'expiration
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => {
                              setDeleteId(item.id);
                              setDeleteType(isCollection ? "collection" : "document");
                            }}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            {t.shareLinks.revoke}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {isCollection && (
                        <Badge variant="outline" className="gap-1 text-xs bg-amber-500/10 text-amber-600 border-amber-500/30">
                          <Files className="w-3 h-3" />
                          Dossier
                        </Badge>
                      )}
                      {expired ? (
                        <Badge variant="destructive" className="text-xs">{t.shareLinks.expired}</Badge>
                      ) : !isCollection && (item as ShareLink).password_hash ? (
                        <Badge variant="outline" className="gap-1 text-xs">
                          <Shield className="w-3 h-3" />
                          {t.shareLinks.protected}
                        </Badge>
                      ) : (
                        <Badge variant="success" className="text-xs">{t.shareLinks.active}</Badge>
                      )}
                      <Badge variant="secondary" className="gap-1 text-xs">
                        <Eye className="w-3 h-3" />
                        {item.view_count} {item.view_count !== 1 ? t.shareLinks.views : t.shareLinks.view}
                      </Badge>
                      {/* Stats button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 gap-1 text-xs"
                        onClick={() => isCollection 
                          ? openCollectionStats(item as DocumentCollection)
                          : fetchComments(item as ShareLink)
                        }
                      >
                        <BarChart3 className="w-3 h-3" />
                        Stats
                      </Button>
                      {/* Comment count badge - only for documents */}
                      {!isCollection && (
                        <Button
                          variant={(item as ShareLink).unread_count > 0 ? "default" : "ghost"}
                          size="sm"
                          className={`h-6 px-2 gap-1 text-xs relative ${(item as ShareLink).unread_count > 0 ? 'animate-pulse' : ''}`}
                          onClick={() => fetchComments(item as ShareLink)}
                        >
                          <MessageCircle className="w-3 h-3" />
                          {(item as ShareLink).comment_count}
                          {(item as ShareLink).unread_count > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive text-destructive-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                              {(item as ShareLink).unread_count > 9 ? '9+' : (item as ShareLink).unread_count}
                            </span>
                          )}
                        </Button>
                      )}
                      <span className="text-muted-foreground">
                        {formatRelative(item.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            )}

            {/* Desktop Table View */}
            {displayedItems.length > 0 && (
            <div className="hidden md:block bg-card border border-border rounded-xl overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">
                    <Checkbox
                      checked={selectedItems.size === displayedItems.length && displayedItems.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>{t.shareLinks.document}</TableHead>
                  <TableHead>{t.shareLinks.permission}</TableHead>
                  <TableHead>Statistiques</TableHead>
                  <TableHead>{t.shareLinks.created}</TableHead>
                  <TableHead>{t.shareLinks.expiration}</TableHead>
                  <TableHead>{t.shareLinks.views}</TableHead>
                  <TableHead>{t.shareLinks.status}</TableHead>
                  <TableHead className="w-[70px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayedItems.map((item) => {
                  const expired = isExpired(item.expires_at);
                  const isCollection = item.type === "collection";
                  const isSelected = selectedItems.has(item.id);
                  
                  return (
                    <TableRow key={item.id} className={isSelected ? 'bg-primary/5' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(item.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {isCollection ? (
                          <Badge variant="outline" className="gap-1 bg-amber-500/10 text-amber-600 border-amber-500/30">
                            <FolderOpen className="w-3 h-3" />
                            Dossier
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="gap-1">
                            <FileText className="w-3 h-3" />
                            Document
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                            isCollection ? "bg-amber-500/10" : "bg-primary/10"
                          }`}>
                            {isCollection ? (
                              <FolderOpen className="w-4 h-4 text-amber-500" />
                            ) : (
                              <FileText className="w-4 h-4 text-primary" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {isCollection 
                                ? (item as DocumentCollection).name
                                : ((item as ShareLink).documents?.title || t.shareLinks.deletedDocument)
                              }
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {isCollection 
                                ? `${(item as DocumentCollection).document_count} documents`
                                : (item as ShareLink).documents?.file_name
                              }
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className="gap-1">
                          {item.permission === "read" ? (
                            <>
                              <Eye className="w-3 h-3" />
                              {t.shareLinks.readOnly}
                            </>
                          ) : (
                            <>
                              <MessageCircle className="w-3 h-3" />
                              {t.shareLinks.readComments}
                            </>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="secondary"
                          size="sm"
                          className="gap-2"
                          onClick={() => isCollection 
                            ? openCollectionStats(item as DocumentCollection)
                            : fetchComments(item as ShareLink)
                          }
                        >
                          <BarChart3 className="w-4 h-4" />
                          <span>Voir</span>
                        </Button>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm" title={formatDate(item.created_at)}>
                          {formatRelative(item.created_at)}
                        </span>
                      </TableCell>
                      <TableCell>
                        {item.expires_at ? (
                          <span className={`text-sm flex items-center gap-1 ${expired ? "text-destructive" : ""}`}>
                            <Calendar className="w-3 h-3" />
                            {formatDate(item.expires_at)}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">{t.shareLinks.never}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{item.view_count}</span>
                      </TableCell>
                      <TableCell>
                        {expired ? (
                          <Badge variant="destructive">{t.shareLinks.expired}</Badge>
                        ) : !isCollection && (item as ShareLink).password_hash ? (
                          <Badge variant="outline" className="gap-1">
                            <Shield className="w-3 h-3" />
                            {t.shareLinks.protected}
                          </Badge>
                        ) : (
                          <Badge variant="success">{t.shareLinks.active}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => copyLink(item.token, isCollection)}>
                              <Copy className="w-4 h-4 mr-2" />
                              {t.shareLinks.copyLink}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openLink(item.token, isCollection)}>
                              <ExternalLink className="w-4 h-4 mr-2" />
                              {t.shareLinks.openLink}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openExpirationEdit(item)}>
                              <CalendarClock className="w-4 h-4 mr-2" />
                              Modifier l'expiration
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => {
                                setDeleteId(item.id);
                                setDeleteType(isCollection ? "collection" : "document");
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              {t.shareLinks.revoke}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            )}
          </>
        )}

        {/* Stats */}
        {(links.length > 0 || collections.length > 0) && (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm text-muted-foreground">{t.shareLinks.stats.totalLinks}</p>
              <p className="text-2xl font-bold">{allItems.length}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm text-muted-foreground">{t.shareLinks.stats.activeLinks}</p>
              <p className="text-2xl font-bold text-success">
                {allItems.filter(i => !isExpired(i.expires_at)).length}
              </p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm text-muted-foreground">{t.shareLinks.stats.totalViews}</p>
              <p className="text-2xl font-bold">{totalViews}</p>
            </div>
            <div className="bg-card border border-border rounded-xl p-4">
              <p className="text-sm text-muted-foreground">Dossiers partagés</p>
              <p className="text-2xl font-bold text-amber-500">{collections.length}</p>
            </div>
          </div>
        )}
      </main>

      {/* Discussion/Stats Panel for Documents */}
      <Sheet open={!!selectedLink} onOpenChange={() => setSelectedLink(null)}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader className="border-b pb-4">
            <SheetTitle className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" />
              {t.shareLinks.discussion.title}
            </SheetTitle>
            {selectedLink && (
              <p className="text-sm text-muted-foreground truncate">
                {selectedLink.documents?.title || t.shareLinks.document}
              </p>
            )}
          </SheetHeader>

          {selectedLink && (
            <Tabs defaultValue="discussion" className="flex-1 flex flex-col min-h-0">
              <TabsList className="grid w-full grid-cols-2 mt-2">
                <TabsTrigger value="discussion" className="flex items-center gap-1.5">
                  <MessageCircle className="w-4 h-4" />
                  Discussion
                </TabsTrigger>
                <TabsTrigger value="analytics" className="flex items-center gap-1.5">
                  <BarChart3 className="w-4 h-4" />
                  Statistiques
                </TabsTrigger>
              </TabsList>

              <TabsContent value="discussion" className="flex-1 flex flex-col min-h-0 mt-4">
                {loadingComments ? (
                  <div className="flex-1 flex items-center justify-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : comments.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-6">
                    <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mb-4">
                      <MessageCircle className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="font-medium mb-2">{t.shareLinks.discussion.noDiscussion}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t.shareLinks.discussion.noDiscussionDesc}
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="flex-1 py-4">
                    <div className="space-y-4 pr-4">
                      {comments.map((comment) => (
                        <div 
                          key={comment.id} 
                          className={`flex gap-3 ${!comment.is_external ? 'flex-row-reverse' : ''}`}
                        >
                          <Avatar className="w-8 h-8 shrink-0">
                            <AvatarFallback className={comment.is_external ? "bg-secondary" : "bg-primary text-primary-foreground"}>
                              {getInitials(comment.author_name, comment.author_email)}
                            </AvatarFallback>
                          </Avatar>
                          <div className={`max-w-[80%] ${!comment.is_external ? 'text-right' : ''}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-medium">
                                {comment.is_external 
                                  ? (comment.author_name || comment.author_email || t.shareLinks.discussion.visitor)
                                  : t.shareLinks.discussion.you
                                }
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatRelative(comment.created_at)}
                              </span>
                            </div>
                            <div className={`rounded-2xl px-4 py-2 ${
                              comment.is_external 
                                ? 'bg-secondary text-secondary-foreground' 
                                : 'bg-primary text-primary-foreground'
                            }`}>
                              <p className="text-sm whitespace-pre-wrap">{comment.body}</p>
                            </div>
                            {comment.image_url && (
                              <div className="mt-2">
                                <img 
                                  src={comment.image_url} 
                                  alt={t.shareLinks.discussion.attachment}
                                  className="rounded-lg max-w-full max-h-48 object-cover"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                {/* Reply input */}
                <div className="border-t pt-4 mt-auto">
                  <div className="flex gap-2">
                    <Input
                      placeholder={t.shareLinks.discussion.replyPlaceholder}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendReply();
                        }
                      }}
                      disabled={sending}
                    />
                    <Button 
                      size="icon" 
                      onClick={sendReply}
                      disabled={!replyText.trim() || sending}
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="analytics" className="flex-1 mt-4">
                <ShareTrackingStats shareLinkId={selectedLink.id} />
              </TabsContent>
            </Tabs>
          )}
        </SheetContent>
      </Sheet>

      {/* Stats Panel for Collections */}
      <Sheet open={!!selectedCollection} onOpenChange={() => setSelectedCollection(null)}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col">
          <SheetHeader className="border-b pb-4">
            <SheetTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-amber-500" />
              Statistiques du dossier
            </SheetTitle>
            {selectedCollection && (
              <p className="text-sm text-muted-foreground truncate">
                {selectedCollection.name}
              </p>
            )}
          </SheetHeader>

          {selectedCollection && (
            <div className="flex-1 mt-4 overflow-auto">
              <div className="mb-4 p-4 bg-muted/30 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <FolderOpen className="w-5 h-5 text-amber-500" />
                  <span className="font-medium">{selectedCollection.document_count} documents</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  Créé {formatRelative(selectedCollection.created_at)}
                </p>
              </div>
              
              <ShareTrackingStats collectionId={selectedCollection.id} />
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t.shareLinks.deleteConfirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteType === "collection" 
                ? "Ce lien de partage de dossier sera révoqué. Les destinataires ne pourront plus accéder aux documents."
                : t.shareLinks.deleteConfirmDesc
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t.shareLinks.revoking}
                </>
              ) : (
                t.shareLinks.revoke
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={showBulkDelete} onOpenChange={setShowBulkDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Révoquer {selectedItems.size} liens ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les {selectedItems.size} liens de partage sélectionnés seront révoqués et les destinataires ne pourront plus accéder aux documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t.common.cancel}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Révocation...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Révoquer {selectedItems.size} liens
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Expiration Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={() => setEditingItem(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-primary" />
              Modifier l'expiration
            </DialogTitle>
            <DialogDescription>
              {editingItem?.type === "collection" 
                ? `Dossier: ${(editingItem as DocumentCollection)?.name}`
                : `Document: ${(editingItem as ShareLink)?.documents?.title || "Sans titre"}`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {editingItem?.expires_at && isPast(new Date(editingItem.expires_at)) && (
              <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2 text-sm text-destructive">
                <Clock className="w-4 h-4" />
                Ce lien a expiré le {formatDate(editingItem.expires_at)}
              </div>
            )}
            
            <Label className="text-sm font-medium mb-3 block">
              Nouvelle date d'expiration
            </Label>
            
            <RadioGroup 
              value={expirationOption} 
              onValueChange={setExpirationOption}
              className="space-y-3"
            >
              <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="7days" id="exp-7days" />
                <Label htmlFor="exp-7days" className="flex-1 cursor-pointer">
                  <span className="font-medium">7 jours</span>
                  <span className="text-muted-foreground text-sm ml-2">
                    (jusqu'au {format(addDays(new Date(), 7), "d MMM yyyy", { locale: lang === 'fr' ? fr : enUS })})
                  </span>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="30days" id="exp-30days" />
                <Label htmlFor="exp-30days" className="flex-1 cursor-pointer">
                  <span className="font-medium">30 jours</span>
                  <span className="text-muted-foreground text-sm ml-2">
                    (jusqu'au {format(addDays(new Date(), 30), "d MMM yyyy", { locale: lang === 'fr' ? fr : enUS })})
                  </span>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="3months" id="exp-3months" />
                <Label htmlFor="exp-3months" className="flex-1 cursor-pointer">
                  <span className="font-medium">3 mois</span>
                  <span className="text-muted-foreground text-sm ml-2">
                    (jusqu'au {format(addMonths(new Date(), 3), "d MMM yyyy", { locale: lang === 'fr' ? fr : enUS })})
                  </span>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="1year" id="exp-1year" />
                <Label htmlFor="exp-1year" className="flex-1 cursor-pointer">
                  <span className="font-medium">1 an</span>
                  <span className="text-muted-foreground text-sm ml-2">
                    (jusqu'au {format(addMonths(new Date(), 12), "d MMM yyyy", { locale: lang === 'fr' ? fr : enUS })})
                  </span>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="custom" id="exp-custom" />
                <Label htmlFor="exp-custom" className="flex-1 cursor-pointer">
                  <span className="font-medium">Date personnalisée</span>
                </Label>
              </div>
              
              {expirationOption === "custom" && (
                <div className="pl-8">
                  <Input
                    type="date"
                    value={customDate}
                    onChange={(e) => setCustomDate(e.target.value)}
                    min={format(new Date(), "yyyy-MM-dd")}
                    className="w-full"
                  />
                </div>
              )}
              
              <div className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-muted/50 transition-colors">
                <RadioGroupItem value="never" id="exp-never" />
                <Label htmlFor="exp-never" className="flex-1 cursor-pointer flex items-center gap-2">
                  <span className="font-medium">Jamais</span>
                  <InfinityIcon className="w-4 h-4 text-muted-foreground" />
                </Label>
              </div>
            </RadioGroup>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingItem(null)} disabled={savingExpiration}>
              Annuler
            </Button>
            <Button onClick={handleSaveExpiration} disabled={savingExpiration || (expirationOption === "custom" && !customDate)}>
              {savingExpiration ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                "Enregistrer"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShareLinks;
